import { computed, ref, watch } from "vue"; // Vue Composition API
import { defineStore } from "pinia";
import { useErrorHandler } from "@/plugins/common/composables/error";
import { useShortcut } from "@modules/app/v2/composables/shortcut";
import store from "@/plugins/store";
import { useFeatureFlagStore } from "@modules/feature-flag/use-feature-flag-store.ts";
import CryptoUtils from "@/utils/crypto.utils";
import { appLocalStorage } from "@/utils/app.storage";
import {
  getHomepage,
  getSignedUrl,
  getShortcuts,
  saveHomepage,
  saveShortcuts,
} from "@modules/user-preference/user-preference-api";
import {
  NonTeacherNonStudentDefaultShortcuts,
  StudentDefaultShortcuts,
  TeacherDefaultShortcuts,
} from "@modules/app/v2/shortcut-constant.ts";
import { USER_PREFERENCE_LOCAL_STORAGE } from "@modules/user-preference/user-preference-constant";
import { FEATURE_FLAG_NAME } from "@modules/feature-flag/feature-flag-helper.ts";
import { ShortcutKey } from "@modules/app/v2/shortcut-types.ts";
import { shortcutSaveRequest } from "@modules/user-preference/user-preference-types.ts";

export const useUserPreferenceStore = defineStore("userPreference", () => {
  const { passiveApiFailHandler } = useErrorHandler();
  const { isFeatureEnabled } = useFeatureFlagStore();
  const { getAvailableShortcuts } = useShortcut();

  const displayNewHomepage = ref<boolean | null>(null);
  const shortcuts = ref<ShortcutKey[]>([]);

  const pinnedShortcuts = computed(() =>
    shortcuts.value.length > 0 ? getAvailableShortcuts(shortcuts.value) : [],
  );

  const isStudent = computed(() => store.getters["auth/isStudent"]);

  const isTeacher = computed(() => store.getters["auth/isTeacher"]);

  const user = computed(() => store.getters["auth/user"]);

  const isOldHomepageFeatureEnabled = computed(() => isFeatureEnabled(FEATURE_FLAG_NAME.HOMEPAGE));

  const isOldHomepageActive = computed(() => {
    if (isOldHomepageFeatureEnabled.value && displayNewHomepage.value === null) {
      return null;
    }
    return isOldHomepageFeatureEnabled.value && !displayNewHomepage.value;
  });

  const defaultShortcuts = computed(() => {
    if (isTeacher.value) {
      return TeacherDefaultShortcuts;
    }

    if (isStudent.value) {
      return StudentDefaultShortcuts;
    }

    return NonTeacherNonStudentDefaultShortcuts;
  });

  const setUserPreferenceSignedUrlParams = async () => {
    const result = await getSignedUrl();

    const url = new URL(result.data);
    const searchParams = url.searchParams;

    appLocalStorage.setItem(
      USER_PREFERENCE_LOCAL_STORAGE.SIGNED_URL_PARAMS,
      searchParams.toString(),
    );
  };

  const getUserPreferenceSignedUrlParams = async (): Promise<URLSearchParams> => {
    let storedParams = appLocalStorage.getItem(USER_PREFERENCE_LOCAL_STORAGE.SIGNED_URL_PARAMS);

    if (!storedParams) {
      await setUserPreferenceSignedUrlParams();
      storedParams = appLocalStorage.getItem(USER_PREFERENCE_LOCAL_STORAGE.SIGNED_URL_PARAMS);
    }

    try {
      return new URLSearchParams(JSON.parse(storedParams) || "");
    } catch {
      return new URLSearchParams();
    }
  };

  const calculateUserHash = async () => {
    return await CryptoUtils.hash(`${user.value.id}${user.value.uuid}`);
  };

  const getLastUpdated = () => {
    return localStorage.getItem(USER_PREFERENCE_LOCAL_STORAGE.LAST_UPDATED);
  };

  const updateLastUpdated = () => {
    localStorage.setItem(USER_PREFERENCE_LOCAL_STORAGE.LAST_UPDATED, Date.now().toString());
  };

  if (!localStorage.getItem(USER_PREFERENCE_LOCAL_STORAGE.LAST_UPDATED)) {
    updateLastUpdated();
  }

  const fetchHomepage = async () => {
    if (!isOldHomepageFeatureEnabled.value) {
      return;
    }

    try {
      if (displayNewHomepage.value != null) {
        return;
      }

      const requestParams = await getUserPreferenceSignedUrlParams();
      requestParams.append("t", getLastUpdated() || "");

      const userHash = await calculateUserHash();

      const response = await getHomepage(user.value.uuid, userHash, requestParams);

      displayNewHomepage.value = response.data.displayNewHomepage || false;
    } catch (error: unknown) {
      console.error("An error occurred while retrieving homepage preference:", error);
      displayNewHomepage.value = true;
    }
  };

  const fetchShortcuts = async () => {
    try {
      const requestParams = await getUserPreferenceSignedUrlParams();
      requestParams.append("t", getLastUpdated() || "");

      const userHash = await calculateUserHash();

      const response = await getShortcuts(user.value.uuid, userHash, requestParams);

      shortcuts.value = response.data.shortcuts;
    } catch (error: unknown) {
      console.error("An error occurred while retrieving shortcuts: " + error);
      shortcuts.value = defaultShortcuts.value;
    }
  };

  const saveHomepageDisplayState = async () => {
    try {
      const newState = !displayNewHomepage.value;

      const response = await saveHomepage({ displayNewHomepage: newState });

      if (response.code == "ok") {
        displayNewHomepage.value = newState;
        updateLastUpdated();
      }
    } catch (error) {
      console.error("An error occurred while saving homepage preference:", error);
      passiveApiFailHandler(error);
    }
  };

  const setShortcuts = async (payload: shortcutSaveRequest) => {
    const response = await saveShortcuts(payload);

    if (response.code == "ok") {
      shortcuts.value = payload.shortcuts;
      updateLastUpdated();
    }

    return response;
  };

  watch(
    isOldHomepageFeatureEnabled,
    (newVal) => {
      if (newVal) {
        fetchHomepage();
      }
    },
    { immediate: true },
  );

  return {
    isOldHomepageFeatureEnabled,
    isOldHomepageActive,
    pinnedShortcuts,
    setUserPreferenceSignedUrlParams,
    fetchHomepage,
    fetchShortcuts,
    saveHomepageDisplayState,
    setShortcuts,
  };
});
